/**
 * Thin fetch wrapper for the Fink Family API.
 * Uses relative URLs so Vite proxy (dev) and same-origin (prod) both work.
 */

const TOKEN_KEY = 'fink_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function api<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(path, {
      ...options,
      headers,
      // Same-origin relative URLs work for LAN + Cloudflare HTTPS
      credentials: 'same-origin',
      cache: 'no-store',
    });
  } catch (err) {
    // Browser TypeError: Failed to fetch — network / tunnel / offline / wrong URL
    const host = typeof location !== 'undefined' ? location.host : '';
    const isLan = /^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host);
    const isCf = host.includes('trycloudflare.com') || host.includes('cloudflare');
    let hint =
      'Cannot reach the server (Failed to fetch). Check that Zeus is on and the tunnel is running.';
    if (isLan) {
      hint =
        'Cannot reach the home server. On cellular/off Wi‑Fi, open the Cloudflare HTTPS link (not the 192.168… LAN address).';
    } else if (isCf) {
      hint =
        'Cloudflare tunnel may be down or this link expired (quick tunnels change after restart). On home Wi‑Fi open http://192.168.1.84:3847 or ask for the new remote URL.';
    }
    const detail = err instanceof Error ? err.message : 'Network error';
    throw new ApiError(0, `${hint} (${detail})`);
  }

  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
    const msg =
      (data as { error?: string })?.error ||
      res.statusText ||
      'Request failed';
    throw new ApiError(res.status, String(msg), data);
  }
  return data as T;
}

// ── Typed helpers ────────────────────────────────────────

export interface PublicUser {
  id: string;
  name: string;
  email: string | null;
  color: string;
  role: string;
  avatar_emoji: string;
}

export interface Calendar {
  id: string;
  name: string;
  color: string;
  owner_user_id: string | null;
  is_shared: number;
  visible: number;
  sort_order: number;
}

export interface CalendarEvent {
  id: string;
  calendar_id: string;
  title: string;
  description: string;
  location: string;
  start_at: string;
  end_at: string;
  all_day: number;
  rrule: string | null;
  timezone: string;
  notes: string;
  created_by: string | null;
}

export interface StickyNote {
  id: string;
  content: string;
  color: string;
  author_id: string | null;
  pinned: number;
}

export interface Todo {
  id: string;
  title: string;
  done: number;
  assignee_id: string | null;
  due_date: string | null;
  category: string;
  sort_order: number;
}

export interface Message {
  id: string;
  content: string;
  author_id: string | null;
  author_name: string;
  created_at: string;
}

export interface DayNote {
  id: string;
  date: string;
  content: string;
  author_id: string | null;
}

export interface WidgetLayoutItem {
  i: string;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  config?: Record<string, unknown>;
}

export const authApi = {
  pin: (pin: string, device?: string) =>
    api<{ token: string; user: PublicUser }>('/api/auth/pin', {
      method: 'POST',
      body: JSON.stringify({ pin, device }),
    }),
  login: (payload: { email?: string; name?: string; password: string }) =>
    api<{ token: string; user: PublicUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  me: () => api<{ user: PublicUser | null }>('/api/auth/me'),
  users: () => api<{ users: PublicUser[] }>('/api/users'),
};

export const calendarApi = {
  list: () => api<{ calendars: Calendar[] }>('/api/calendars'),
  events: (params?: { from?: string; to?: string; expand?: string }) => {
    const q = new URLSearchParams();
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    if (params?.expand) q.set('expand', params.expand);
    const qs = q.toString();
    return api<{ events: CalendarEvent[] }>(`/api/events${qs ? `?${qs}` : ''}`);
  },
  createEvent: (body: {
    title: string;
    calendar_id: string;
    start_at: string;
    end_at: string;
    description?: string;
    location?: string;
    all_day?: boolean;
    rrule?: string | null;
    notes?: string;
  }) =>
    api<{ event: CalendarEvent }>('/api/events', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateEvent: (
    id: string,
    body: {
      title?: string;
      calendar_id?: string;
      start_at?: string;
      end_at?: string;
      description?: string;
      location?: string;
      all_day?: boolean;
      rrule?: string | null;
      notes?: string;
    }
  ) => {
    const master = id.includes('__') ? id.split('__')[0] : id;
    return api<{ event: CalendarEvent }>(
      `/api/events/${encodeURIComponent(master)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      }
    );
  },
  deleteEvent: (id: string) => {
    // Strip recurring occurrence suffix masterId__ISO
    const master = id.includes('__') ? id.split('__')[0] : id;
    return api<{ ok: boolean }>(`/api/events/${encodeURIComponent(master)}`, {
      method: 'DELETE',
    });
  },
  day: (date: string) =>
    api<{ date: string; events: CalendarEvent[]; notes: DayNote[] }>(
      `/api/days/${date}/events`
    ),
  createDayNote: (date: string, content: string) =>
    api<{ note: DayNote }>('/api/day-notes', {
      method: 'POST',
      body: JSON.stringify({ date, content }),
    }),
  deleteDayNote: (id: string) =>
    api<{ ok: boolean }>(`/api/day-notes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
};

export type AlertLevel = 'L1' | 'L2' | 'L3' | 'L911';

export interface FamilyAlert {
  id: string;
  level: AlertLevel;
  title: string;
  body: string;
  author_id: string | null;
  author_name: string;
  active: number;
  created_at: string;
  dismissed_at: string | null;
}

export const alertApi = {
  active: () => api<{ alerts: FamilyAlert[] }>('/api/alerts/active'),
  create: (payload: { level: AlertLevel; body: string; title?: string }) =>
    api<{ alert: FamilyAlert }>('/api/alerts', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  dismiss: (id: string) =>
    api<{ alert: FamilyAlert }>(`/api/alerts/${encodeURIComponent(id)}/dismiss`, {
      method: 'POST',
    }),
};

export const notesApi = {
  stickies: () => api<{ stickies: StickyNote[] }>('/api/stickies'),
  createSticky: (body: { content?: string; color?: string }) =>
    api<{ sticky: StickyNote }>('/api/stickies', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateSticky: (id: string, body: Partial<StickyNote>) =>
    api<{ sticky: StickyNote }>(`/api/stickies/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteSticky: (id: string) =>
    api<{ ok: boolean }>(`/api/stickies/${id}`, { method: 'DELETE' }),
  todos: () => api<{ todos: Todo[] }>('/api/todos'),
  createTodo: (body: { title: string; assignee_id?: string | null; category?: string }) =>
    api<{ todo: Todo }>('/api/todos', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateTodo: (id: string, body: Partial<{ title: string; done: boolean; assignee_id: string | null }>) =>
    api<{ todo: Todo }>(`/api/todos/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteTodo: (id: string) =>
    api<{ ok: boolean }>(`/api/todos/${id}`, { method: 'DELETE' }),
  messages: () => api<{ messages: Message[] }>('/api/messages'),
  postMessage: (content: string) =>
    api<{ message: Message }>('/api/messages', {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),
};

export const widgetApi = {
  layout: (profile = 'default') =>
    api<{ device_profile: string; layout: WidgetLayoutItem[] }>(
      `/api/widgets/layout?profile=${profile}`
    ),
  saveLayout: (layout: WidgetLayoutItem[], profile = 'default') =>
    api<{ layout: WidgetLayoutItem[] }>(
      `/api/widgets/layout?profile=${profile}`,
      { method: 'PUT', body: JSON.stringify({ layout }) }
    ),
  catalog: () =>
    api<{
      widgets: Array<{
        type: string;
        name: string;
        description: string;
        defaultW: number;
        defaultH: number;
      }>;
    }>('/api/widgets/catalog'),
  settings: () => api<{ settings: Record<string, string> }>('/api/settings'),
  patchSettings: (settings: Record<string, string>) =>
    api('/api/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    }),
};

export const haApi = {
  status: () => api<Record<string, unknown>>('/api/ha/status'),
  toggle: (entity_id: string) =>
    api('/api/ha/toggle', {
      method: 'POST',
      body: JSON.stringify({ entity_id }),
    }),
  service: (domain: string, service: string, entity_id?: string) =>
    api('/api/ha/service', {
      method: 'POST',
      body: JSON.stringify({ domain, service, entity_id }),
    }),
  blink: () => api<Record<string, unknown>>('/api/blink'),
  arm: (mode: 'arm_away' | 'arm_home' | 'disarm') =>
    api('/api/blink/arm', {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),
};

export const weatherApi = {
  get: () => api<Record<string, unknown>>('/api/weather'),
  getMulti: () =>
    api<{ locations: Record<string, unknown>[]; updated_at: string }>(
      '/api/weather?multi=1'
    ),
};
